import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Key, ShieldStar } from "@phosphor-icons/react";

const LOGO_BSVAC = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/rcm1qmbk_BSVAC%20Patch.png";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const required = !!user?.must_change_password;
  const alreadyHasUsername = !!user?.username;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (next === current) { toast.error("New password must differ from current"); return; }
    setBusy(true);
    try {
      const payload = { current_password: current, new_password: next };
      // Only send username on first login when the field is offered and typed.
      if (required && !alreadyHasUsername && username.trim()) {
        payload.username = username.trim();
      }
      const { data } = await api.post("/auth/change-my-password", payload);
      setUser(data);
      toast.success("Password updated — welcome to the console");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-10" data-testid="change-password-page">
      <div className="w-full max-w-md surface-grid p-8 bg-white">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_BSVAC} alt="BSVAC" className="h-12 w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-black tracking-tight text-sm">BSVAC / CBVAC</div>
            <div className="tab-tag text-slate-500">First-time password setup</div>
          </div>
        </div>

        <div className={`flex items-start gap-2 mb-5 p-3 rounded-sm ${required ? "bg-amber-50 border border-amber-300" : "bg-slate-100 border border-slate-200"}`}>
          <Key size={18} weight="bold" className={required ? "text-amber-700 mt-0.5" : "text-slate-700 mt-0.5"} />
          <div className="text-xs leading-relaxed">
            {required ? (
              <>
                <strong className="block mb-0.5">Your account is on the default password.</strong>
                You must set a new personal password before you can use the console.
              </>
            ) : (
              <>Rotate your password. The new password must be at least 8 characters.</>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="change-pw-form">
          <div>
            <Label className="tab-tag">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="rounded-sm" required data-testid="cp-current" autoFocus />
          </div>
          <div>
            <Label className="tab-tag">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-sm" required data-testid="cp-new" />
            <p className="text-[11px] text-slate-500 mt-1">At least 8 characters. Use letters, numbers, and a symbol.</p>
          </div>
          <div>
            <Label className="tab-tag">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm" required data-testid="cp-confirm" />
          </div>

          {required && !alreadyHasUsername && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-3 space-y-1.5" data-testid="cp-username-block">
              <Label className="tab-tag text-emerald-900">Pick a username <span className="normal-case tracking-normal text-emerald-800 font-normal">(optional — use it to sign in instead of your email)</span></Label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="rounded-sm bg-white"
                placeholder="e.g. jsmith"
                data-testid="cp-username"
                autoComplete="off"
                maxLength={32}
              />
              <p className="text-[11px] text-emerald-800">3–32 characters. Letters, numbers, dot, dash or underscore. Skip if you want to keep signing in with just your email.</p>
            </div>
          )}

          <Button type="submit" disabled={busy} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="cp-submit">
            <ShieldStar size={14} weight="bold" className="mr-1" />{busy ? "Saving…" : "Set new password"}
          </Button>
          {!required && (
            <Button type="button" variant="outline" className="w-full rounded-sm" onClick={() => nav(-1)} data-testid="cp-cancel">Cancel</Button>
          )}
          {required && (
            <Button type="button" variant="ghost" className="w-full rounded-sm text-slate-500" onClick={async () => { await logout(); nav("/login"); }} data-testid="cp-logout">
              Sign out
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
